"use client";

import { useMemo } from "react";
import CatalogLookCard from "@/components/dashboard/catalog/CatalogLookCard";
import CatalogToolCard from "@/components/dashboard/catalog/CatalogToolCard";
import {
  getCatalogNavSections,
  type CatalogNavItem,
} from "@/components/dashboard/catalog/catalogNav";

function NavCard({ item, onClose }: { item: CatalogNavItem; onClose: () => void }) {
  if (item.preset) {
    return (
      <div onClick={onClose}>
        <CatalogToolCard tool={item.preset} />
      </div>
    );
  }

  return (
    <div onClick={onClose}>
      <CatalogLookCard
        href={item.href}
        title={item.title}
        tagline={item.tagline}
        images={item.look?.images ?? (item.poster ? [item.poster] : [])}
        toolId={item.toolId}
        cardType={item.look?.cardType ?? "look"}
        cardId={item.id}
        section="all-tools"
        overlaySrc={item.look?.overlaySrc}
        borderFrames={item.look?.borderFrames}
      />
    </div>
  );
}

interface AllToolsModalProps {
  open: boolean;
  onClose: () => void;
}

export default function AllToolsModal({ open, onClose }: AllToolsModalProps) {
  const sections = useMemo(() => getCatalogNavSections(), []);

  return (
    <div className="fixed inset-0 pointer-events-none z-[10004]" aria-hidden={!open}>
      <div
        className={`absolute inset-0 bg-black transition-opacity duration-300 ${open ? "opacity-50" : "opacity-0"}`}
        style={{ pointerEvents: open ? "auto" : "none" }}
        onClick={onClose}
      />

      <div
        className={`fixed inset-y-0 left-1/2 -translate-x-1/2 w-full max-w-3xl bg-gray-50 shadow-2xl flex flex-col transition-all duration-300 ${
          open ? "opacity-100 scale-100" : "opacity-0 scale-95 pointer-events-none"
        }`}
        style={{ pointerEvents: open ? "auto" : "none" }}
      >
        <div className="px-6 py-4 border-b border-gray-200 bg-white flex items-center justify-between shrink-0">
          <h2 className="font-bold text-gray-900 text-lg">All Tools</h2>
          <button
            aria-label="Close"
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 bg-gray-100 hover:bg-gray-200 p-2 rounded-2xl transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto overscroll-contain">
          <div className="p-4 sm:p-6 space-y-10">
            {sections.map((section) => (
              <section key={section.id}>
                <h3
                  className="text-xl font-bold text-gray-800 mb-1"
                  style={{ fontFamily: "var(--font-merriweather)" }}
                >
                  {section.title}
                </h3>
                {section.tagline ? (
                  <p className="text-gray-500 text-sm mb-4">{section.tagline}</p>
                ) : (
                  <div className="mb-4" />
                )}
                <div className="grid grid-cols-2 gap-x-4 gap-y-8">
                  {section.items.map((item) => (
                    <NavCard key={item.id} item={item} onClose={onClose} />
                  ))}
                </div>
              </section>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
